/**
 * Settings → Super-admin → Workspaces: een workspace aanmaken, leeg of met
 * het Meridiaan Bank-voorbeeld (`exampleWorkspace.ts`).
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api } from "../api/client";
import { rememberWorkpackage, useSession } from "../state/session";
import { ArchiveSection } from "./ArchiveSection";
import { createExampleWorkspace } from "./exampleWorkspace";
import { ld } from "./ledgerUi";

function explain(err: unknown): string {
  if (err instanceof ApiError) return err.body?.detail ?? err.body?.title ?? err.message;
  if (err instanceof Error) return err.message;
  return String(err);
}

export function WorkspacesTab({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const me = useSession((s) => s.me);
  const uiLanguage: "nl" | "en" = i18n.language.startsWith("nl") ? "nl" : "en";
  const [name, setName] = useState("");
  const [language, setLanguage] = useState<"nl" | "en">(uiLanguage);
  const [adminEmail, setAdminEmail] = useState(me?.email ?? "");
  const [busy, setBusy] = useState<"create" | "example" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{
    message: string;
    workspaceId: string;
    mine: boolean;
    /** Waar het model staat; anders opent de app het eerste workpackage (Base). */
    target?: { workpackageId: string; branchId: string };
  } | null>(null);

  const finish = (
    message: string,
    workspaceId: string,
    mine: boolean,
    target?: { workpackageId: string; branchId: string },
  ) => {
    setDone({ message, workspaceId, mine, ...(target ? { target } : {}) });
  };

  const create = async () => {
    setBusy("create");
    setError(null);
    setDone(null);
    try {
      const ws = await api.createWorkspace({
        name: name.trim(),
        default_language: language,
        ...(adminEmail.trim() ? { admin_email: adminEmail.trim() } : {}),
      });
      setName("");
      finish(
        t("settings.superAdmin.workspacesCreated", { name: ws.name, email: ws.admin_email }),
        ws.id,
        ws.admin_email === me?.email?.toLowerCase(),
      );
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(null);
    }
  };

  const example = async () => {
    setBusy("example");
    setError(null);
    setDone(null);
    try {
      const exampleName = t("settings.superAdmin.exampleName");
      const { workspaceId, workpackageId, branchId, result } = await createExampleWorkspace(
        exampleName,
        uiLanguage,
      );
      if (result.failed?.length) {
        const lost = result.failed.reduce((n, f) => n + f.statements, 0);
        throw new Error(t("settings.superAdmin.exampleIncomplete", { count: lost }));
      }
      finish(
        t("settings.superAdmin.exampleCreated", {
          name: exampleName,
          concepts: result.concepts_inserted,
          relations: result.relations_inserted,
          diagrams: result.diagrams_inserted,
        }),
        workspaceId,
        true,
        { workpackageId, branchId },
      );
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(null);
    }
  };

  const open = async (
    workspaceId: string,
    target?: { workpackageId: string; branchId: string },
  ) => {
    // De nieuwe workspace staat pas na een verse `me` in de lijst.
    if (target) rememberWorkpackage(workspaceId, target.workpackageId, target.branchId);
    await useSession.getState().refreshMe();
    await useSession.getState().switchWorkspace(workspaceId);
    onClose();
  };

  return (
    <div className="flex flex-col gap-8">
      <form
        className="flex max-w-xl flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (busy === null && name.trim()) void create();
        }}
      >
        <h4 className={ld.sectionTitle}>{t("settings.superAdmin.workspacesNewHeading")}</h4>
        <div>
          <label htmlFor="sa-new-ws-name" className={ld.label}>
            {t("settings.superAdmin.workspacesName")}
          </label>
          <input
            id="sa-new-ws-name"
            value={name}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
            className={ld.input}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-[180px_1fr]">
          <div>
            <label htmlFor="sa-new-ws-lang" className={ld.label}>
              {t("settings.superAdmin.workspacesLanguage")}
            </label>
            <select
              id="sa-new-ws-lang"
              value={language}
              onChange={(e) => setLanguage(e.target.value as "nl" | "en")}
              className={ld.input}
            >
              <option value="en">{t("settings.superAdmin.languageEn")}</option>
              <option value="nl">{t("settings.superAdmin.languageNl")}</option>
            </select>
          </div>
          <div>
            <label htmlFor="sa-new-ws-admin" className={ld.label}>
              {t("settings.superAdmin.workspacesAdmin")}
            </label>
            <input
              id="sa-new-ws-admin"
              type="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              className={ld.input}
            />
          </div>
        </div>
        <div>
          <button type="submit" disabled={busy !== null || !name.trim()} className={ld.primary}>
            {t("settings.superAdmin.workspacesCreate")}
          </button>
        </div>
      </form>

      <section className="flex max-w-xl flex-col items-start gap-3 border-(--ld-rule) border-t pt-8">
        <h4 className={ld.sectionTitle}>{t("settings.superAdmin.exampleHeading")}</h4>
        <p className={ld.sectionIntro}>{t("settings.superAdmin.exampleIntro")}</p>
        <button
          type="button"
          disabled={busy !== null}
          onClick={() => void example()}
          className={ld.secondary}
        >
          {busy === "example"
            ? t("settings.superAdmin.exampleBusy")
            : t("settings.superAdmin.exampleButton")}
        </button>
      </section>

      {done && (
        <div role="status" className={`flex items-center justify-between gap-3 ${ld.success}`}>
          <span>{done.message}</span>
          {done.mine && (
            <button
              type="button"
              onClick={() => void open(done.workspaceId, done.target)}
              className="shrink-0 font-medium underline-offset-2 hover:underline"
            >
              {t("settings.superAdmin.workspacesOpen")}
            </button>
          )}
        </div>
      )}
      {error && (
        <p role="alert" className={ld.error}>
          {error}
        </p>
      )}

      <ArchiveSection onOpen={(id) => void open(id)} />
    </div>
  );
}
